import React, { useState, useMemo, useEffect } from "react";
import Loader from "../../components/Common/Loader";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { nutrientUnits } from "../../utils/Constant/Constant";

const RecipeDetailsModal = ({ recipeDetails, visible, onClose }) => {
  const { trans } = useCustomTranslation();
  const [loadingImage, setLoadingImage] = useState(false);

  const hasRecipe = !!recipeDetails;

  const MISSING_NUTRIENT_FALLBACK = "0";


  const formatValueWithUnit = (
    val,
    unit,
    fallback = MISSING_NUTRIENT_FALLBACK
  ) => {
    if (val === null || val === undefined) return `${fallback} ${unit}`;
    const strVal = String(val).trim();
    if (strVal === "" || strVal.toLowerCase() === "nan")
      return `${fallback} ${unit}`;
    if (/[a-zA-Z]/.test(strVal)) return strVal;
    return `${strVal} ${unit}`;
  };

  const macroRatios = useMemo(() => {
    const carbsRaw =
      recipeDetails?.nutrientsPerserving?.carbs_g ||
      recipeDetails?.nutrientsPerserving?.carbs_pct;
    const proteinRaw =
      recipeDetails?.nutrientsPerserving?.protein_g ||
      recipeDetails?.nutrientsPerserving?.protein_pct;
    const fatRaw =
      recipeDetails?.nutrientsPerserving?.fat_g ||
      recipeDetails?.nutrientsPerserving?.fat_pct;

    const carbsGrams = parseFloat(carbsRaw) || 0;
    const proteinGrams = parseFloat(proteinRaw) || 0;
    const fatGrams = parseFloat(fatRaw) || 0;

    const totalCalories = proteinGrams * 4 + carbsGrams * 4 + fatGrams * 9;
    if (!totalCalories) return { carbs: 0, protein: 0, fat: 0 };

    return {
      carbs: (carbsGrams * 4) / totalCalories,
      protein: (proteinGrams * 4) / totalCalories,
      fat: (fatGrams * 9) / totalCalories,
    };
  }, [recipeDetails]);

  const dotColor = {
    carbs: "#89008e",
    protein: "#574dec",
    fat: "#dceb00",
  };

  const formatNutrientKey = (key) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  if (!visible) return null;

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 1060 }}
      onClick={(e) => {
        if (e.target.className === "modal-overlay") onClose();
      }}
    >
      <div
        className="modal-content recipe-modal-content"
        style={{ maxWidth: "600px", maxHeight: "90vh", overflowY: "auto" }}
      >
        <div className="modal-header">
          <h4>Recipe Details</h4>
          <button className="modal-close" onClick={onClose}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="ai-modal-body">
          {!hasRecipe ? (
            <Loader />
          ) : (
            <div>
              {recipeDetails?.mainImage && (
                <div className="position-relative">
                  <img
                    className="image-card-drawer"
                    alt={recipeDetails.title || "Recipe"}
                    src={recipeDetails.mainImage}
                    onLoad={() => setLoadingImage(false)}
                    onError={() => setLoadingImage(false)}
                    style={{ display: loadingImage ? "none" : "block" }}
                  />
                  {loadingImage && <Loader message="" />}
                </div>
              )}

              {/* Title */}
              <h3 className="drawer-recipe-name text-gray-700">
                {recipeDetails?.title}
              </h3>

              <div className="detail-container-drawer">
                <hr />
                {/* Time / Servings / Ingredients */}
                <div className="d-flex">
                  <p className="detail-time-recipe">
                    {(recipeDetails?.prepTime || recipeDetails?.totalTime) ?? "N/A"}
                  </p>
                  <p className="detail-time-recipe">
                    {`${recipeDetails?.servings ?? 0}`}
                  </p>
                  <p className="detail-time-recipe">
                    {`${recipeDetails?.ingredientsCount ?? 0} ${trans(
                      "recipes.ingredients"
                    )}`}
                  </p>
                </div>

                <hr className="mt-0" />

                {/* Ingredients */}
                <h4>{trans("recipes.Ingredients")}</h4>
                <ul className="blue-dot-list">
                  {(recipeDetails?.ingredientList || []).map((item, idx) => (
                    <li key={idx} className="fs-5">
                      {`${item.quantityMetric} ${item.unitMetric} ${
                        item?.ingredient || " "
                      }`}
                    </li>
                  ))}
                </ul>

                {/* Instructions */}
                <h4>{trans("recipes.instructions")}</h4>
                <ul className="blue-dot-list">
                  {(recipeDetails?.instructions || []).flatMap((instr, idx) =>
                    instr
                      .split(",,")
                      .filter((step) => step.trim().length > 0)
                      .map((step, stepIdx) => (
                        <li key={`${idx}-${stepIdx}`} className="fs-5">
                          {formatNutrientKey(step.trim())}
                        </li>
                      ))
                  )}
                </ul>

                <hr />
                {/* Nutrition */}
                <h4>{trans("recipes.nutritionPerServing")}</h4>

                {/* Macro progress bar */}
                <div className="mt-2 progress-bar-recipe mb-3">
                  <div
                    className="bar-carb"
                    style={{ width: `${(macroRatios.carbs || 0) * 100}%` }}
                  />
                  <div
                    className="bar-protein"
                    style={{ width: `${(macroRatios.protein || 0) * 100}%` }}
                  />
                  <div
                    className="bar-fat"
                    style={{ width: `${(macroRatios.fat || 0) * 100}%` }}
                  />
                </div>

                {/* Macro indicators */}
                {["carbs", "protein", "fat"].map((macro) => (
                  <React.Fragment key={macro}>
                    <div className="indicator-nutrition d-flex align-items-center">
                      <div
                        className="rounded-circle ml-2 mr-2"
                        style={{
                          backgroundColor: dotColor[macro],
                          width: 15,
                          height: 15,
                        }}
                      />

                      <span className="fs-5">
                        {trans(`recipes.${macro}`)} (
                        {((macroRatios?.[macro] || 0) * 100).toFixed(2)}%)
                      </span>

                      <div className="ml-auto mb-0">
                        <p className="mb-0">
                          {formatValueWithUnit(
                            recipeDetails?.nutrientsPerserving?.[
                              `${macro}_g`
                            ] || recipeDetails?.nutrientsPerserving?.[macro],
                            "g"
                          )}
                        </p>
                      </div>
                    </div>
                    <hr />
                  </React.Fragment>
                ))}

                <ul>
                  {Object.entries(recipeDetails?.nutrientsPerserving || {})
                    .filter(([key]) =>
                      [
                        "calories",
                        "calcium",
                        "iron",
                        "magnesium",
                        "potassium",
                        "sodium",
                        "sugar",
                        "vitaminC",
                        "fiber",
                      ].includes(key)
                    )
                    .map(([key, value]) => {
                      const formattedKey = formatNutrientKey(key);
                      const unit = nutrientUnits[key] || "";

                      return (
                        <li key={key} className="p-0 m-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <p>{formattedKey}</p>
                            <div>
                              <p>{formatValueWithUnit(value, unit)}</p>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailsModal;